<script lang="ts" setup>
import {
  ClassroomEmailInfo,
  ClassroomEmailInfoRecipient,
} from "~/classes/models/classroom-emails/classroom-email-info.model";
import { ClassroomEmail } from "~/classes/models/classroom-emails/classroom-email.model";
import { FileResource } from "~/classes/models/education-resources/education-resource.model";
import { ExportDocumentFormat } from "~/types/enums/ExportDocumentFormat.enum";

const props = defineProps({
  documentId: {
    type: String,
    required: true,
  },
});

const sheetController = useNewSheetController();

const appUserData = useAppUserDataStore();
const { appUser, isPublicDemo } = storeToRefs(appUserData);

const submittedDocumentStore = useSubmittedDocumentStore(props.documentId);
const { submittedDocument, studentLinkedAccounts } = storeToRefs(
  submittedDocumentStore
);

const emit = defineEmits(["close"]);

const classroomEmail = ref<ClassroomEmail | undefined>(undefined);

watch(
  () => appUser.value?.displayName,
  () => {
    if (classroomEmail.value) {
      classroomEmail.value.displayName = appUser.value?.displayName ?? "";
    }
  }
);

const defaultSubject = (submittedDocument: SubmittedDocument) => {
  let subject = "";

  const subjectsProps = [];

  if (submittedDocument.classroomName) {
    subjectsProps.push(submittedDocument.classroomName);
  }

  if (submittedDocument.assignmentName) {
    subjectsProps.push(submittedDocument.assignmentName);
  }

  subjectsProps.push("Graded");

  return subjectsProps.join(" - ");
};

onMounted(() => {
  const recipients = studentLinkedAccounts.value.map((studentLinkedAccount) => {
    return {
      studentId: studentLinkedAccount.studentId,
      label: studentLinkedAccount.type,
      email: studentLinkedAccount.email,
      hasRead: false,
      viewCount: 0,
      lastViewedAtTimestamp: 0,
    } as {
      studentId?: string;
      label?: string;
      email: string;
      token?: string;
      hasRead: boolean;
      viewCount?: number;
      lastViewedAtTimestamp?: number;
    };
  });

  classroomEmail.value = new ClassroomEmail({
    userId: useCurrentUID()!,
    classroomId: submittedDocument.value?.classroomId,
    label: `${submittedDocument.value?.studentName} - ${submittedDocument.value?.assignmentName} - Graded`,
    displayName: appUser.value?.displayName ?? "",
    subject: defaultSubject(submittedDocument.value!),
    body: [
      { insert: "Your submission for " },
      {
        insert: submittedDocument.value?.assignmentName,
        attributes: { bold: true },
      },
      {
        insert:
          " has been graded. Please look at my comments and let me know if you have any questions.\n\nView here: ",
      },
      {
        insert: `${window.location.protocol}//students.${window.location.host}/documents/student/review/${props.documentId}`,
        attributes: {
          link: `${window.location.protocol}//students.${window.location.host}/documents/student/review/${props.documentId}`,
          target: "_blank",
        },
      },
    ],
    attachments: [],
    createdAtTimestamp: 0,
    isArchived: false,
    info: new ClassroomEmailInfo({
      recipients: recipients,
      userId: useCurrentUID()!,
    }),
  });

  classroomEmail.value.displayName = appUser.value?.displayName ?? "";
});

const attachGradedSubmission = ref(false);
const selectedExportType = ref<ExportDocumentFormat>(ExportDocumentFormat.docx);

const isSendingEmail = ref(false);

const sendEmail = async () => {
  if (classroomEmail.value == undefined) {
    useBaseToast("Oops! Something went wrong. Please try again.", "error");
    return;
  }

  if (classroomEmail.value.info == undefined) {
    useBaseToast("Oops! Something went wrong. Please try again.", "error");
    return;
  }

  if (classroomEmail.value.label == "") {
    useBaseToast("Please enter a label.", "error");
    return;
  }

  if (classroomEmail.value.subject == "") {
    useBaseToast("Please enter a subject.", "error");
    return;
  }

  if (classroomEmail.value.info.recipients.length == 0) {
    useBaseToast("Please select at least one recipient.", "error");
    return;
  }

  isSendingEmail.value = true;

  if (attachGradedSubmission.value == true) {
    const gradedSubmissionFileData = await useExportLoadedDocument(
      props.documentId,
      selectedExportType.value,
      true
    );

    if (gradedSubmissionFileData == undefined) {
      useBaseToast("Oops! Something went wrong. Please try again.", "error");
      isSendingEmail.value = false;
      return;
    }

    const customFile: CustomFile = {
      data: gradedSubmissionFileData,
      name: useGenerateGradedFileName(
        submittedDocument.value!,
        selectedExportType.value
      ),
      size: gradedSubmissionFileData.size,
      type: gradedSubmissionFileData.type,
    };

    const mediaObject = await useFileUploader().createMediaObject(
      customFile,
      `/classroom-resources/${submittedDocument.value?.classroomId}/emails`,
      false
    );

    if (mediaObject == undefined) {
      useBaseToast("Oops! Something went wrong. Please try again.", "error");
      isSendingEmail.value = false;
      return;
    }

    const task = await useFileUploader().uploadFile(
      gradedSubmissionFileData,
      mediaObject
    );

    mediaObject.mediaHref = await useFileUploader().getDownloadURL(task.ref);

    const fileResource = new FileResource({
      id: useGenerateUID(),
      label: mediaObject.originalFileName,
      mediaObject: mediaObject,
      mimeType: mediaObject.mimeType,
    });

    classroomEmail.value.attachments.push(fileResource);
  }

  await appUserData.saveUser();
  await classroomEmail.value.save();
  await classroomEmail.value.sendEmail();

  useBaseToast("Email sent successfully!", "success");
  emit("close");

  sheetController.value.close();

  isSendingEmail.value = false;
};

const addRecipient = (recipient: ClassroomEmailInfoRecipient) => {
  classroomEmail.value?.info?.recipients.push(recipient);
};

const removeRecipient = (recipientIndex: number) => {
  classroomEmail.value?.info?.recipients.splice(recipientIndex, 1);
};
</script>

<template>
  <BaseSheetComponent :controller="sheetController" width="800px">
    <template #trigger>
      <slot />
    </template>
    <template #content>
      <div
        class="p-4 h-full flex flex-col overflow-auto"
        v-if="classroomEmail && appUser"
      >
        <BaseFormComponent>
          <template #label> Label </template>
          <BaseTextInput v-model="classroomEmail.label" />
        </BaseFormComponent>
        <BaseFormComponent>
          <template #label> From </template>
          <BaseTextInput v-model="appUser.displayName" />
        </BaseFormComponent>
        <BaseFormComponent>
          <template #label> To </template>
          <div class="flex flex-col">
            <ClassroomEmailsFormRecipients
              v-if="classroomEmail.info && classroomEmail.classroomId"
              :show-select-recipients="false"
              :classroom-email="classroomEmail"
              :classroom-email-info="classroomEmail.info"
              :classroom-id="classroomEmail.classroomId"
              :recipients="classroomEmail.info.recipients"
              :disabled="false"
              @add-recipient="addRecipient"
              @remove-recipient="removeRecipient"
            />
          </div>
        </BaseFormComponent>
        <BaseFormComponent>
          <template #label> Subject </template>
          <BaseTextInput v-model="classroomEmail.subject" />
        </BaseFormComponent>
        <BaseFormComponent>
          <label class="text-h5"> Body </label>
          <div>
            <BaseDocumentTextEditor
              toolbar-id="quill-email-toolbar"
              class="mb-2 max-h-[400px]"
              v-model="classroomEmail.body"
              :show-formatting-options="true"
              :auto-focus="true"
              :document-id="documentId"
            />
          </div>
        </BaseFormComponent>
        <BaseFormComponent>
          <template #label> Attach graded submission? </template>
          <div class="flex flex-col">
            <BaseToggle v-model="attachGradedSubmission" />
            <TransitionsCollapsible>
              <div
                v-if="attachGradedSubmission"
                class="flex flex-row gap-4 mt-4"
              >
                <BaseCard
                  class="flex flex-col p-4 w-[120px] cursor-pointer hover:bg-light-default aspect-square justify-center items-center"
                  :class="{
                    '!bg-primary-default !bg-opacity-20 hover:!bg-opacity-30':
                      selectedExportType === ExportDocumentFormat.docx,
                  }"
                  @click="selectedExportType = ExportDocumentFormat.docx"
                >
                  <icon
                    name="file-icons:microsoft-word"
                    class="mb-2"
                    size="40"
                  />

                  <span> Word </span>
                </BaseCard>
                <BaseCard
                  class="flex flex-col p-4 w-[120px] cursor-pointer hover:bg-light-default aspect-square justify-center items-center"
                  :class="{
                    '!bg-primary-default !bg-opacity-20 hover:!bg-opacity-30':
                      selectedExportType === ExportDocumentFormat.pdf,
                  }"
                  @click="selectedExportType = ExportDocumentFormat.pdf"
                >
                  <icon
                    name="grommet-icons:document-pdf"
                    class="mb-2 text-[#FF0000]"
                    size="40"
                  />
                  <span> PDF </span>
                </BaseCard>
              </div>
            </TransitionsCollapsible>
          </div>
        </BaseFormComponent>
        <BaseButton
          class="mt-4 shrink-0"
          @click="sendEmail"
          :show-spinner="isSendingEmail"
        >
          Send Email
        </BaseButton>
      </div>
    </template>
  </BaseSheetComponent>
</template>

<style scoped></style>
